import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { AuditPromiseClient } from '@trustero/trustero-api-web/lib/audit/audit_grpc_web_pb'
import {
  AuditRecord,
  CloseAuditRequest,
} from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { useGrpcRevalidateByMethod } from 'src/components'
import { useMemo, useEffect } from 'react'
import { useFilters } from 'src/Utils/filter/filter.hooks'
import { Filter, Transform } from 'src/Utils/filter/filter'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { useFilterBarValues } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.context'
import { SearchableData } from 'src/Utils/search/search.types'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'

export const useCloseAudit = (): ((auditId: string) => void) => {
  const client = useAuthorizedGrpcClient(AuditPromiseClient)
  const revalidate = useGrpcRevalidateByMethod()

  return async (auditId: string) => {
    await client.closeAudit(new CloseAuditRequest().setAuditId(auditId))
    await revalidate(AuditPromiseClient.prototype.getAudits)
  }
}

// Filter configuration for audits
const auditFilterConfig: Filter[] = [
  {
    key: FilterParam.AUDIT_FRAMEWORKS,
    property: 'audit_frameworks',
  },
  {
    key: FilterParam.DATE_RANGE,
    property: 'date',
    type: 'date',
  },
  {
    key: FilterParam.AUDIT_STATUS,
    property: 'audit_status',
  },
]

// Transform function for audit data
const transformAudit: Transform<AuditRecord> = (audit) => {
  const frameworks = audit.getComplianceFrameworksList?.() || []
  const frameworkNames: string[] = frameworks
    .map(
      (framework: ComplianceFramework) =>
        framework.getName?.() || framework.getModelId?.(),
    )
    .filter(Boolean)

  const date: string[] = []
  const startDate = audit?.getStartDate()
  if (startDate) {
    date.push(startDate.toDate().toISOString())
  }
  const endDate = audit?.getEndDate()
  if (endDate) {
    date.push(endDate.toDate().toISOString())
  }

  const status = audit.getIsClosed() ? 'Closed' : 'Open'

  return {
    audit_frameworks: frameworkNames,
    date: date,
    audit_status: status,
  }
}

// Hook for extracting audit filter data and registering values in context
export const useAuditFiltersAndSearchData = (
  audits: AuditRecord[],
): SearchableData<AuditRecord>[] => {
  const { setValues, registerTransform } = useFilterBarValues()
  const searchableAudits = useSearchableAudits(audits)

  // Extract unique frameworks and date ranges from audits
  const filterData = useMemo(() => {
    if (!audits.length) {
      return { frameworks: [], auditStatuses: [] }
    }

    const frameworkSet = new Set<string>()
    const auditStatusSet = new Set<string>()

    audits.forEach((audit) => {
      // Extract frameworks from audit
      const frameworks = audit.getComplianceFrameworksList?.() || []
      frameworks.forEach((framework: ComplianceFramework) => {
        const frameworkName = framework.getName?.() || framework.getModelId?.()
        if (frameworkName) {
          frameworkSet.add(frameworkName)
        }
      })

      // Extract status from audit
      const status = audit.getIsClosed() ? 'Closed' : 'Open'
      auditStatusSet.add(status)
    })

    return {
      frameworks: Array.from(frameworkSet).sort(),
      auditStatuses: Array.from(auditStatusSet).sort(),
    }
  }, [audits])

  // Register framework values in the context
  useEffect(() => {
    setValues('audit_frameworks', filterData.frameworks)
  }, [filterData.frameworks, setValues])

  // Register audit status values in the context
  useEffect(() => {
    setValues('audit_status', filterData.auditStatuses)
  }, [filterData.auditStatuses, setValues])

  // Register transformation function for frameworks
  useEffect(() => {
    registerTransform(FilterParam.AUDIT_FRAMEWORKS, (frameworks) =>
      frameworks.map((framework) => ({
        value: typeof framework === 'string' ? framework : framework.value,
        label: typeof framework === 'string' ? framework : framework.label,
      })),
    )
  }, [registerTransform])

  // Register transformation function for audit status
  useEffect(() => {
    registerTransform(FilterParam.AUDIT_STATUS, (auditStatuses) =>
      auditStatuses.map((auditStatus) => ({
        value:
          typeof auditStatus === 'string' ? auditStatus : auditStatus.value,
        label:
          typeof auditStatus === 'string' ? auditStatus : auditStatus.label,
      })),
    )
  }, [registerTransform])

  return searchableAudits
}

// Hook for making audit data searchable
export const useSearchableAudits = (
  data: AuditRecord[] | undefined,
): SearchableData<AuditRecord>[] => {
  return useMemo(() => {
    if (!data) {
      return []
    }

    return prepareSearchableData(
      data,
      (audit) => audit.getId(),
      (audit) => {
        const auditName = audit.getName?.() || ''
        const frameworks = audit.getComplianceFrameworksList?.() || []
        const frameworkNames = frameworks
          .map(
            (framework: ComplianceFramework) =>
              framework.getName?.() || framework.getModelId?.(),
          )
          .filter(Boolean)
          .join(', ')

        const status = audit.getIsClosed() ? 'Closed' : 'Open'

        return [
          auditName.toLowerCase(),
          frameworkNames.toLowerCase(),
          status.toLowerCase(),
          audit.getStartDate?.()?.toDate?.()?.toISOString?.() || '',
          audit.getEndDate?.()?.toDate?.()?.toISOString?.() || '',
        ].filter((value) => !!value)
      },
    )
  }, [data])
}

// Hook for applying filters to audit data
export const useFilteredAudits = (audits: AuditRecord[]): AuditRecord[] => {
  return useFilters({
    filters: auditFilterConfig,
    data: audits,
    transform: transformAudit,
  })
}
